import { AI_CREDIT_PACKS, AI_CREDIT_PACK_CATALOGUE } from 'definitions/billing-plans';
import type { BillingSettingsManager } from '@/state/billing/useBillingSettings';
import { Button } from '@/components/ui/button';
import { formatMinorUnits } from 'helpers/formatters';
import { aiFeatureLabel, formatBillingDate, formatUnits } from '@/state/billing/billing-format';
import { Panel } from '../../ConsolePanel';

type AiCreditsCardProps = { manager: BillingSettingsManager };

/**
 * The account's AI credits: what can be spent now, where it comes from, and the
 * packs that top it up.
 *
 * Each pack goes through the credit-pack branch of the checkout endpoint
 * (`buyCreditPack`), which selects that pack's one-time product and omits the
 * trial. Packs are valid for twelve months, and are spent after the plan's
 * included credits, which reset each period and do not carry over.
 *
 * Buying is an Owner/Admin action of whoever pays, so Members, and a client's own
 * users when an agency pays for them, see the balance and no buttons. The credits are
 * the account's own: an agency's client has its own balance, bought and spent apart.
 * When packs cannot be sold on this deployment the buttons are disabled with a
 * reason, not removed: a silently absent button looks like the balance cannot be
 * topped up at all.
 */
export const AiCreditsCard = ({ manager }: AiCreditsCardProps) => {
  const { state, canManageBilling, checkout, accountId } = manager;
  const billingState = state.billingState;
  if (!billingState) return null;
  // Credits are bought by whoever pays for the account: an agency for its client's.
  const canBuy = canManageBilling && billingState.isPayer;

  const { aiCredits, aiSpend, creditPacksAvailable, currency } = billingState;
  // Billed by invoice or free: the included credits are a monthly allowance, not a plan's, and packs top it up.
  const onAllowance = billingState.billingMode !== 'card';

  return (
    <Panel
      tourAnchor="billing-credits"
      title="AI credits"
      description={
        onAllowance
          ? 'Credits for AI features. Your monthly allowance renews on the 1st; a pack tops it up and is spent after it.'
          : "Credits for AI features. Your plan's credits renew each period."
      }
    >
      {aiCredits ? (
        <div className="grid gap-4">
          <div className="grid gap-1">
            <span className="metric-value text-foreground">
              {aiCredits.balance > 0 && aiCredits.balance < 1 ? 'Under 1' : formatUnits(Math.floor(aiCredits.balance))}
            </span>
            <span className="supporting">
              {aiCredits.balance > 0
                ? 'credits you can use now'
                : aiCredits.includedExpiresAt
                  ? `AI features are paused. They resume when your ${onAllowance ? 'allowance renews' : "plan's credits reset"} on ${formatBillingDate(aiCredits.includedExpiresAt)}, or when you buy a pack.`
                  : 'AI features are paused. They resume when you buy a pack or start a plan.'}
            </span>
          </div>

          <dl className="m-0 grid gap-1">
            <div className="flex justify-between gap-4">
              <dt className="supporting">{onAllowance ? 'Monthly allowance' : 'From your plan'}</dt>
              <dd className="m-0 num">
                {formatUnits(Math.floor(aiCredits.includedRemaining))} of {formatUnits(aiCredits.includedGranted)},
                resets {formatBillingDate(aiCredits.includedExpiresAt)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="supporting">Bought</dt>
              <dd className="m-0 num">
                {aiCredits.packRemaining > 0
                  ? `${formatUnits(Math.floor(aiCredits.packRemaining))}, earliest expires ${formatBillingDate(aiCredits.nextPackExpiresAt)}`
                  : 'None'}
              </dd>
            </div>
          </dl>

          {aiSpend.length > 0 && (
            <div className="grid gap-1">
              <span className="micro-label">Used in the last 30 days</span>
              <dl className="m-0 grid gap-1">
                {aiSpend.map((item) => (
                  <div key={item.feature} className="flex justify-between gap-4">
                    <dt className="supporting">{aiFeatureLabel(item.feature)}</dt>
                    <dd className="m-0 num">{formatUnits(Math.round(item.credits))} credits</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {canBuy && !creditPacksAvailable && <p className="supporting m-0">Credit packs are not available yet.</p>}
          {canBuy && (
            <div className="flex flex-wrap gap-2">
              {AI_CREDIT_PACKS.map((pack) => {
                const { credits, price } = AI_CREDIT_PACK_CATALOGUE[pack];
                return (
                  <Button
                    key={pack}
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-lg text-body"
                    onClick={() => checkout.buyCreditPack({ pack, accountId })}
                    disabled={!creditPacksAvailable || checkout.isStarting}
                    title="Valid for 12 months"
                  >
                    {formatUnits(credits)} credits, {formatMinorUnits(price, currency)}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <p className="supporting m-0">No AI credits yet. They start with your plan.</p>
      )}
    </Panel>
  );
};
